import React from 'react';
import { Field, BaseFieldProps, WrappedFieldProps } from 'redux-form';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
  UseFormSetValue,
} from 'react-hook-form';
import { PredicatesFilter } from '@common/components/predicates-filter';
import { PredicatesFilterHookForm } from '@common/components/predicates-filter/predicates-filter-hook-form';
import { ESearchPredicateTypes } from '@common/definitions';
import type { PredicatesFilterOwnProps } from '@common/components/predicates-filter';

const fieldWrapper =
  <P extends Record<string, any>, C = React.ComponentType<any>>(component: C) => (props: BaseFieldProps &
    Omit<P, keyof WrappedFieldProps>) => <Field {...props} component={component} />;
type AudienceProps = React.ComponentProps<typeof PredicatesFilter>;
export const AudienceInput = fieldWrapper<AudienceProps>(PredicatesFilter);

/**
 * React Hook Form wrapper for AudienceInput
 * By default the form validation mode is set to `mode: 'onSubmit'`.
 * This means the validation will be shown after submitting the form.
 * You probably want to set this option to `all` to have immediate feedback when a field does not pass the added rules.
 * useForm({ mode: 'all' });
 */
export function AudienceInputHookForm<T extends FieldValues>(
  props: Omit<PredicatesFilterOwnProps, 'formValues'> & {
    name: UseControllerProps<T>['name'];
    control: Control<T>;
    setValue?: UseFormSetValue<T>;
    tooltips?: PredicatesFilterOwnProps['tooltips'];
  }
) {
  const {
 name, control, setValue, tooltips = {}, ...restProps
} = props;

  // Register the field with default value
  useController({
    name,
    control,
    defaultValue: {
      predicate_type: ESearchPredicateTypes.MATCH_ANY,
      predicates: [],
    } as any,
  });

  // Construct nested field paths
  const predicateTypeName = `${String(name)}.predicate_type` as any;
  const predicatesName = `${String(name)}.predicates` as any;

  return (
    <PredicatesFilterHookForm
      name={name}
      control={control}
      setValue={setValue}
      predicateTypeName={predicateTypeName}
      predicatesName={predicatesName}
      tooltips={tooltips}
      {...restProps}
    />
  );
}
